import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { cartActions } from './actions'
import {
  selectCartItems,
  selectIsCartOpen,
  selectTotalItems,
  selectTotalPrice,
  selectScrapPreference
} from './selectors'
import { Battery } from '../../../shared/types/battery'
import { getEffectiveUnitPrice } from '../../../constants/checkout'
import { CartItem } from './types'

/**
 * Função auxiliar para gerar ID do item do carrinho
 */
const generateCartItemId = (productId: string) => {
  return productId
}

function getItemWillReturnScrap(item: CartItem, scrapPreference: boolean | null): boolean {
  return item.willReturnScrap ?? scrapPreference ?? true
}

/**
 * Hook para usar funcionalidades de Cart com Redux
 * Mantém compatibilidade com a interface antiga do CartContext
 */
export const useCart = () => {
  const dispatch = useAppDispatch()
  const items = useAppSelector(selectCartItems)
  const isOpen = useAppSelector(selectIsCartOpen)
  const totalItems = useAppSelector(selectTotalItems)
  const totalPrice = useAppSelector(selectTotalPrice)
  const scrapPreference = useAppSelector(selectScrapPreference)

  const addItem = useCallback((
    product: Battery,
    quantity: number = 1,
    showToast: boolean = true,
    willReturnScrap?: boolean
  ) => {
    dispatch(cartActions.addItem({ product, quantity, showToast, willReturnScrap }))
  }, [dispatch])

  const setScrapPreference = useCallback((value: boolean | null) => {
    dispatch(cartActions.setScrapPreference(value))
  }, [dispatch])

  const removeItem = useCallback((itemId: string) => {
    dispatch(cartActions.removeItem(itemId))
  }, [dispatch])

  const updateQuantity = useCallback((itemId: string, quantity: number) => {
    dispatch(cartActions.updateQuantity({ id: itemId, quantity }))
  }, [dispatch])

  const updateItemScrapPreference = useCallback((productId: string, willReturnScrap: boolean) => {
    dispatch(cartActions.updateItemScrapPreference({ productId, willReturnScrap }))
  }, [dispatch])

  const clearCart = useCallback(() => {
    dispatch(cartActions.clearCart())
  }, [dispatch])

  const toggleCart = useCallback(() => {
    dispatch(cartActions.toggleCart())
  }, [dispatch])

  const openCart = useCallback(() => {
    dispatch(cartActions.openCart())
  }, [dispatch])

  const closeCart = useCallback(() => {
    dispatch(cartActions.closeCart())
  }, [dispatch])

  const getTotalItems = useCallback(() => {
    return totalItems
  }, [totalItems])

  /** Preço total considerando sucata por item. isCampinas = true por padrão (ex.: sidebar). */
  const getTotalPrice = useCallback((isCampinas: boolean = true) => {
    return items.reduce((sum, item) => {
      const allowsScrap = (item.product as { allowsScrapReturn?: boolean }).allowsScrapReturn === true
      const willReturn = getItemWillReturnScrap(item, scrapPreference)
      const unitPrice = allowsScrap
        ? getEffectiveUnitPrice(
            item.product.price,
            item.product.weight ?? 0,
            isCampinas,
            willReturn
          )
        : item.product.price
      return sum + unitPrice * item.quantity
    }, 0)
  }, [items, scrapPreference])

  /** Subtotal base (sem acréscimo de sucata), para compatibilidade. */
  const getBaseSubtotal = useCallback(() => totalPrice, [totalPrice])

  const getCartItemId = useCallback((product: Battery) => {
    return generateCartItemId(product.id)
  }, [])

  return {
    state: {
      items,
      isOpen
    },
    scrapPreference,
    addItem,
    setScrapPreference,
    removeItem,
    updateQuantity,
    updateItemScrapPreference,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    getTotalItems,
    getTotalPrice,
    getBaseSubtotal,
    getCartItemId
  }
}

